import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserAuthContext } from '../UserAuthContext';
import { apiUrl } from '../config/api';
import { SPANISH_APP_PATTERNS, SPANISH_APP_TRANSLATIONS } from '../i18n/spanishAppTranslations';
import { SPANISH_LEGAL_TRANSLATIONS } from '../i18n/spanishLegalTranslations';
import { SPANISH_SELLER_PATTERNS, SPANISH_SELLER_TRANSLATIONS } from '../i18n/spanishSellerTranslations';
import { SPANISH_TAXONOMY_TRANSLATIONS } from '../i18n/spanishTaxonomyTranslations';
import { SPANISH_SITE_GAP_PATTERNS, SPANISH_SITE_GAP_TRANSLATIONS } from '../i18n/spanishSiteGapsTranslations';

export const LANGUAGE_STORAGE_KEY = 'eulua_language';

// The /es prefix is the canonical, crawlable URL for the Spanish version of every
// localizable page (see App.js). When present, it is the source of truth for the
// language of that pageview — it overrides both the stored preference and any
// geolocation-based auto-detection, so search engines always get consistent,
// distinct content for /path vs /es/path.
export const getLanguageFromPath = (pathname) => (
  pathname === '/es' || pathname.startsWith('/es/') || pathname === '/sell/es' ? 'es' : 'en'
);

export const stripLanguagePrefix = (pathname) => {
  if (pathname === '/es') return '/';
  if (pathname.startsWith('/es/')) return pathname.slice(3);
  return pathname;
};

export const withLanguagePrefix = (pathname, language) => {
  const bare = stripLanguagePrefix(pathname);
  if (language !== 'es') return bare;
  return bare === '/' ? '/es' : `/es${bare}`;
};

const SPANISH_COUNTRY_CODES = new Set([
  'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'ES', 'GQ',
  'GT', 'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'UY', 'VE',
]);

const SPANISH_COUNTRIES = new Set([
  'argentina', 'bolivia', 'chile', 'colombia', 'costa rica', 'cuba',
  'dominican republic', 'ecuador', 'el salvador', 'equatorial guinea',
  'guatemala', 'honduras', 'mexico', 'nicaragua', 'panama', 'paraguay',
  'peru', 'puerto rico', 'spain', 'uruguay', 'venezuela',
  'espana', 'guinea ecuatorial', 'republica dominicana',
]);

const ES = {
  // Header and navigation
  'Language': 'Idioma',
  'Spanish': 'Español',
  'Welcome!': '¡Bienvenido!',
  'or': 'o',
  'Log In': 'Iniciar sesión',
  'sign up': 'registrarte',
  'Sell': 'Vender',
  'Favorites List': 'Lista de favoritos',
  'My Account': 'Mi cuenta',
  'Finances': 'Finanzas',
  'Account': 'Cuenta',
  'Orders': 'Pedidos',
  'My Requests': 'Mis solicitudes',
  'Browsing History': 'Historial de navegación',
  'Watchlist': 'Favoritos',
  'Seller Dashboard': 'Panel de vendedor',
  'Log Out': 'Cerrar sesión',
  'My Orders': 'Mis pedidos',
  'Menu': 'Menú',
  'Search': 'Buscar',
  'Search products...': 'Buscar productos...',
  'Search products': 'Buscar productos',
  'Search Products': 'Buscar productos',
  'Open navigation menu': 'Abrir menú de navegación',
  'Close navigation menu': 'Cerrar menú de navegación',
  'Construction': 'Construcción',
  'Agricultural': 'Agricultura',
  'Livestock and By-Products': 'Ganadería y subproductos',
  'Chemicals': 'Químicos',
  'Housing': 'Vivienda',
  'Equipment': 'Equipamiento',
  'Most Important': 'Más importantes',
  'More Categories': 'Más categorías',
  'No items available': 'No hay elementos disponibles',
  'Previous promotion': 'Promoción anterior',
  'Next promotion': 'Promoción siguiente',
  'available products.': 'productos disponibles.',

  // Location and cart
  'Select Location': 'Seleccionar ubicación',
  'Select Delivery Location': 'Seleccionar ubicación de entrega',
  'Estimated from your network. Search or use your device location to make it more precise.': 'Estimación basada en tu red. Busca o usa la ubicación de tu dispositivo para hacerla más precisa.',
  'Search Location': 'Buscar ubicación',
  'Finding your location…': 'Buscando tu ubicación…',
  'Use my current location': 'Usar mi ubicación actual',
  'City, state or country': 'Ciudad, estado o país',
  'Cancel': 'Cancelar',
  'Loading...': 'Cargando...',
  'Empty cart!': '¡Tu carrito está vacío!',
  'View Cart': 'Ver carrito',
  'Checkout': 'Finalizar compra',
  'Subtotal': 'Subtotal',
  'Cart': 'Carrito',

  // Hero and landing-page sections
  'Buy From Verified Suppliers': 'Compra a proveedores verificados',
  'Explore products for your business, compare sellers and purchase with shipping and taxes confirmed before payment.': 'Explora productos para tu empresa, compara vendedores y compra con el envío y los impuestos confirmados antes del pago.',
  'Shop Products': 'Comprar productos',
  'Become a Seller': 'Conviértete en vendedor',
  'Shop From Multiple Sellers': 'Compra a múltiples vendedores',
  "Purchase from different suppliers in one place while tracking each seller's products, delivery date and fulfillment status.": 'Compra a diferentes proveedores en un solo lugar y consulta los productos, la fecha de entrega y el estado de cada vendedor.',
  'See How It Works': 'Ver cómo funciona',
  'Browse Products': 'Explorar productos',
  'Grow Your Business on Eulua': 'Haz crecer tu empresa en Eulua',
  'Apply as a seller, publish your catalog and reach verified buyers with clear commercial terms.': 'Solicita ser vendedor, publica tu catálogo y llega a compradores verificados con condiciones comerciales claras.',
  'Explore the Marketplace': 'Explorar el mercado',
  'Quick Access:': 'Acceso rápido:',
  'Discounts!': '¡Descuentos!',
  'Most popular': 'Más populares',
  'Top Sellers': 'Mejores vendedores',
  'Just Added': 'Recién agregados',
  'Latest Auctions': 'Últimas subastas',
  'Bid before these auctions close': 'Ofrece antes de que cierren estas subastas',
  'Auction': 'Subasta',
  'Auctions': 'Subastas',
  'Auction listing': 'Publicación de subasta',
  'Auction overview': 'Resumen de subastas',
  'Pending Auctions': 'Subastas pendientes',
  'Track scheduled and active auctions, bids and closing times from one place.': 'Controla las subastas programadas y activas, sus ofertas y horarios de cierre desde un solo lugar.',
  'Pending': 'Pendientes',
  'Live': 'Activas',
  'Scheduled': 'Programadas',
  'Total bids': 'Total de ofertas',
  'No pending auctions': 'No hay subastas pendientes',
  'Create an auction to start receiving bids from buyers.': 'Crea una subasta para comenzar a recibir ofertas de compradores.',
  'bids': 'ofertas',
  'Single listing': 'Publicación individual',
  'Add a new product': 'Agregar un producto nuevo',
  'Create a complete listing with pricing, stock, delivery and product images.': 'Crea una publicación completa con precio, inventario, entrega e imágenes.',
  'Create product': 'Crear producto',
  'Create an auction': 'Crear una subasta',
  'Create Auction': 'Crear subasta',
  'Create auction': 'Crear subasta',
  'Set a minimum bid and a closing date for one product.': 'Define una oferta mínima y una fecha de cierre para un producto.',
  'Set a minimum bid for the complete listed lot and a closing date.': 'Define una oferta mínima por el lote completo publicado y una fecha de cierre.',
  'Publish a product with a minimum bid and a fixed closing date': 'Publica un producto con una oferta mínima y una fecha de cierre fija',
  'Publish a full-lot auction with a minimum bid and a fixed closing date': 'Publica una subasta por el lote completo con una oferta mínima y una fecha de cierre fija',
  'Auction settings': 'Configuración de la subasta',
  'Auction currency': 'Moneda de la subasta',
  'Bid currency': 'Moneda de la oferta',
  'Pricing & Discounts': 'Precios y descuentos',
  'All bids for this lot use this currency.': 'Todas las ofertas por este lote utilizan esta moneda.',
  'Fixed volume discounts use this same currency.': 'Los descuentos fijos por volumen utilizan esta misma moneda.',
  'Minimum bid': 'Oferta mínima',
  'Minimum bid for the full lot': 'Oferta mínima por el lote completo',
  'Latest bid': 'Última oferta',
  'Latest bid for the full lot': 'Última oferta por el lote completo',
  'This amount covers the entire volume listed below, not one unit.': 'Este monto cubre todo el volumen publicado abajo, no una unidad.',
  'This amount covers the entire volume listed below, not one unit. The first buyer may bid this amount; every following bid must be at least 1.5% higher.': 'Este monto cubre todo el volumen publicado abajo, no una unidad. El primer comprador puede ofertar este monto; cada oferta posterior debe ser al menos 1.5% mayor.',
  'Lot volume': 'Volumen del lote',
  'Every bid covers this entire lot; it is not a price per unit.': 'Cada oferta cubre este lote completo; no es un precio por unidad.',
  'Bid for the complete lot': 'Oferta por el lote completo',
  'Each bid covers the entire listed volume:': 'Cada oferta cubre todo el volumen publicado:',
  'It is not a price per unit.': 'No es un precio por unidad.',
  'Closing date and time': 'Fecha y hora de cierre',
  'Start date and time': 'Fecha y hora de inicio',
  'Starts': 'Inicia',
  'Starts in': 'Inicia en',
  'Time remaining': 'Tiempo restante',
  'Closes': 'Cierra',
  'Closes in': 'Cierra en',
  'Ended': 'Finalizada',
  'Preview Auction': 'Vista previa de la subasta',
  'Auction Preview': 'Vista previa de la subasta',
  'Publish Auction': 'Publicar subasta',
  'Publishing Auction...': 'Publicando subasta...',
  'Listing type': 'Tipo de publicación',
  'All listings': 'Todas las publicaciones',
  'Fixed price': 'Precio fijo',
  'View the standard order process': 'Ver el proceso normal de pedidos',
  'Your bid': 'Tu oferta',
  'Your bid for the full lot': 'Tu oferta por el lote completo',
  'Minimum next bid': 'Próxima oferta mínima',
  'Each new bid must be at least 1.5% higher than the current bid.': 'Cada nueva oferta debe ser al menos 1.5% mayor que la oferta actual.',
  'Place bid': 'Ofertar',
  'Placing bid...': 'Enviando oferta...',
  'Your bid was placed.': 'Tu oferta fue enviada.',
  'We will email you once if this bid is surpassed. A new bid from you can generate one new notice if it is surpassed again.': 'Te enviaremos un solo correo si esta oferta es superada. Si vuelves a ofertar, esa nueva oferta podrá generar un nuevo aviso único si también es superada.',
  'Could not place bid': 'No se pudo enviar la oferta',
  'Auction not found': 'No se encontró la subasta',
  'Enter a valid bid amount': 'Ingresa un monto de oferta válido',
  'Minimum bid must be greater than zero': 'La oferta mínima debe ser mayor que cero',
  'Auction start date is required': 'La fecha de inicio de la subasta es obligatoria',
  'Auction closing date must be after its start date': 'La fecha de cierre de la subasta debe ser posterior a la fecha de inicio',
  'This listing is not an auction': 'Esta publicación no es una subasta',
  'This auction has not started': 'Esta subasta todavía no ha comenzado',
  'This auction has ended': 'Esta subasta ha finalizado',
  'You cannot bid on your own auction': 'No puedes ofertar en tu propia subasta',
  'Log in to place a bid.': 'Inicia sesión para ofertar.',
  'Log in to bid': 'Inicia sesión para ofertar',
  'Enter a minimum bid, a valid start date and a closing date after it.': 'Ingresa una oferta mínima, una fecha de inicio válida y una fecha de cierre posterior.',
  'Loading auctions...': 'Cargando subastas...',
  'Loading recent products...': 'Cargando productos recientes...',
  'Could not load auctions': 'No se pudieron cargar las subastas',
  'Could not load recent products': 'No se pudieron cargar los productos recientes',
  'No active auctions are available right now.': 'No hay subastas activas en este momento.',
  'No recent products are available.': 'No hay productos recientes disponibles.',
  'The bid does not include transportation costs, which may vary. The seller may reject a bid. If the winning bid is accepted, the order is created and follows the standard order process in Orders.': 'La oferta no incluye los costos de movilización, que pueden variar. El vendedor puede rechazar una oferta. Si acepta la oferta ganadora, se crea el pedido y sigue el proceso normal en Pedidos.',
  'Every bid is for the full listed lot, not a price per unit. The bid does not include transportation costs, which may vary. The seller may reject a bid. If the winning bid is accepted, the order is created and follows the standard order process in Orders.': 'Cada oferta corresponde al lote completo publicado, no a un precio por unidad. La oferta no incluye los costos de movilización, que pueden variar. El vendedor puede rechazar una oferta. Si acepta la oferta ganadora, se crea el pedido y sigue el proceso normal en Pedidos.',
  'Auction published successfully!': '¡Subasta publicada correctamente!',
  'Auction order:': 'Orden de subasta:',
  'The winning bid covers the complete listed volume and cannot be changed. Transportation or mobilization is not included and must be quoted separately.': 'La oferta ganadora cubre el volumen completo publicado y no puede modificarse. El transporte o la movilización no están incluidos y deben cotizarse por separado.',
  'Review shipping, taxes, the locked winning bid and delivery dates, then accept or reject the order.': 'Revisa el envío, los impuestos, la oferta ganadora bloqueada y las fechas de entrega; luego acepta o rechaza el pedido.',
  'this auction order is Pending and waiting for your confirmation or rejection. Review shipping, taxes, the locked winning bid and delivery dates, then accept or reject the order.': 'este pedido de subasta está Pendiente y espera tu confirmación o rechazo. Revisa el envío, los impuestos, la oferta ganadora bloqueada y las fechas de entrega; luego acepta o rechaza el pedido.',
  'Auction · complete lot': 'Subasta · lote completo',
  'in lot': 'en el lote',
  'Complete-lot bid': 'Oferta por el lote completo',
  'volume': 'de volumen',
  'Sponsored': 'Patrocinado',
  'FAQs': 'Preguntas frecuentes',
  'Buy from multiple suppliers in one place': 'Compra a múltiples proveedores en un solo lugar',
  'Add the products you need and shop confidently. Each seller confirms shipping, taxes and delivery dates before payment.': 'Agrega los productos que necesitas y compra con confianza. Cada vendedor confirma el envío, los impuestos y las fechas de entrega antes del pago.',
  'Shop Now': 'Comprar ahora',
  '1. Shop products': '1. Busca productos',
  'Search available inventory and review the seller, specifications, minimum quantities and pickup options.': 'Busca en el inventario disponible y revisa el vendedor, las especificaciones, las cantidades mínimas y las opciones de recogida.',
  '2. Review seller terms': '2. Revisa las condiciones del vendedor',
  'After you place the order request, each seller confirms product availability, taxes, shipping and the expected delivery date.': 'Después de enviar la solicitud de pedido, cada vendedor confirma la disponibilidad, los impuestos, el envío y la fecha prevista de entrega.',
  '3. Complete your purchase': '3. Completa tu compra',
  'Submit your bank transfer securely. The purchase is marked as paid after Eulua verifies the funds.': 'Envía tu transferencia bancaria de forma segura. La compra se marca como pagada después de que Eulua verifica los fondos.',
  'Main Categories': 'Categorías principales',
  'Discover our wide range of raw materials organized by category': 'Descubre nuestra amplia variedad de materias primas organizadas por categoría',
  'See All →': 'Ver todo →',
  'Why shop on Eulua?': '¿Por qué comprar en Eulua?',
  'Products for your business from verified suppliers with transparent purchasing terms': 'Productos para tu empresa de proveedores verificados y con condiciones de compra transparentes',
  'Seller Verification': 'Verificación de vendedores',
  'Approved sellers display verification separately from real performance and verified buyer reviews.': 'Los vendedores aprobados muestran su verificación por separado del rendimiento real y de las reseñas de compradores verificados.',
  'Flexible Fulfillment': 'Entrega flexible',
  'Each seller confirms delivery or pickup costs and expected dates before the buyer submits payment.': 'Cada vendedor confirma los costos de entrega o recogida y las fechas previstas antes de que el comprador envíe el pago.',
  'Transparent Pricing': 'Precios transparentes',
  'Product prices, seller taxes, shipping and the Eulua service fee are shown separately before payment.': 'Los precios, impuestos del vendedor, envío y tarifa de servicio de Eulua se muestran por separado antes del pago.',
  'Recommended Products': 'Productos recomendados',
  'Company':'Compania',
  'Chemicals':'Quimicos',
  'Construction':'Construccion',
  'Housing':'Vivienda',
  'Equipment':'Equipamento',
  'Livestock and By-products':'Ganaderia y subproductos',
  'Agricultural':'Agricultura',
  'Category':'Categoria',
  'Sign in to receive suggestions based on your favorites, browsing history and past orders.': 'Inicia sesión para recibir sugerencias basadas en tus favoritos, historial de navegación y pedidos anteriores.',
  'Start exploring products; recommendations will adapt as your browsing history grows.': 'Comienza a explorar productos; las recomendaciones se adaptarán a medida que crezca tu historial de navegación.',
  'Sign In': 'Iniciar sesión',
  'View Product': 'Ver producto',
  'Preparing your recommendations...': 'Preparando tus recomendaciones...',
  'Recommendations could not be loaded right now.': 'No se pudieron cargar las recomendaciones en este momento.',
  'No products are available for recommendation yet.': 'Aún no hay productos disponibles para recomendar.',
  'Sponsored · Paid advertising': 'Patrocinado · Publicidad pagada',
  'Best Sellers': 'Más vendidos',
  'The most purchased products in the last 7 days': 'Los productos más comprados en los últimos 7 días',
  'Loading best sellers...': 'Cargando los productos más vendidos...',
  'There are no best sellers available right now.': 'No hay productos más vendidos disponibles en este momento.',
  'Hot Deals': 'Ofertas especiales',
  'Limited time offers with amazing discounts': 'Ofertas por tiempo limitado con grandes descuentos',
  'Loading hot deals...': 'Cargando ofertas especiales...',
  'Hot deals could not be loaded right now.': 'No se pudieron cargar las ofertas en este momento.',
  'There are no hot deals available right now.': 'No hay ofertas especiales disponibles en este momento.',
  'Fresh arrivals you don\'t want to miss': 'Novedades que no te querrás perder',
  'Cargando productos recientes...': 'Cargando productos recientes...',
  'No hay productos recientes disponibles.': 'No hay productos recientes disponibles.',
  'Units sold': 'Unidades vendidas',
  'Rating': 'Calificación',
  'Seller': 'Vendedor',
  'New': 'Nuevo',
  'Best-selling products': 'Productos más vendidos',
  'No published products are available.': 'No hay productos publicados disponibles.',
  'View Seller Profile': 'Ver perfil del vendedor',
  'Top Sellers in Your Region': 'Mejores vendedores de tu región',
  'Discover leading sellers ranked by city, state, and country relevance.': 'Descubre vendedores destacados clasificados por relevancia de ciudad, estado y país.',
  'Meet the sellers behind the most purchased products on Eulua.': 'Conoce a los vendedores de los productos más comprados en Eulua.',
  'Allow personalization to discover the highest-ranked sellers near you.': 'Permite la personalización para descubrir los vendedores mejor clasificados cerca de ti.',
  'Set your location to discover top sellers in your region.': 'Configura tu ubicación para descubrir los mejores vendedores de tu región.',
  'There are no qualifying seller sales in your region yet.': 'Aún no hay ventas de vendedores que cumplan los requisitos en tu región.',
  'There are no qualifying seller sales yet.': 'Aún no hay ventas de vendedores que cumplan los requisitos.',
  'Loading top sellers...': 'Cargando los mejores vendedores...',
  'Top sellers could not be loaded right now.': 'No se pudieron cargar los mejores vendedores en este momento.',
  'Loading the best-selling products...': 'Cargando los productos más vendidos...',
  'Best sellers could not be loaded right now.': 'No se pudieron cargar los productos más vendidos en este momento.',

  // Homepage product showcase, top sellers toggle and categories
  'Recommended': 'Recomendados',
  'Product lists': 'Listas de productos',
  'Price on request': 'Precio a consultar',
  'Your region': 'Tu región',
  'All regions': 'Todas las regiones',
  'Seller ranking area': 'Zona del ranking de vendedores',
  'Find products and supplies for your business, organized by category': 'Encuentra productos e insumos para tu empresa, organizados por categoría',
  'Product prices, seller taxes and shipping are shown clearly before payment, without an Eulua sales commission.': 'Los precios de los productos, los impuestos del vendedor y el envío se muestran claramente antes del pago, sin comisión de venta de Eulua.',

  // Project quote explainer
  'Project quotes': 'Cotizaciones de proyectos',
  'One request. Multiple seller quotes.': 'Una solicitud. Varias cotizaciones de vendedores.',
  'Source project materials that need custom pricing, delivery terms or a seller recommendation.': 'Consigue materiales para proyectos que requieren precios personalizados, condiciones de entrega o la recomendación de un vendedor.',
  'Explore seller quote requests': 'Explorar solicitudes de cotización',
  'Describe the project': 'Describe el proyecto',
  'Add the material, quantity, location, fulfillment preference and reference photos.': 'Agrega el material, la cantidad, la ubicación, la preferencia de entrega o recogida y fotos de referencia.',
  'Sellers prepare quotes': 'Los vendedores preparan cotizaciones',
  'Eligible sellers review the request and respond with their price, costs and estimated delivery date.': 'Los vendedores elegibles revisan la solicitud y responden con su precio, costos y fecha estimada de entrega.',
  'Choose with confidence': 'Elige con confianza',
  'Compare every response in one place and accept the quote that best fits your project.': 'Compara todas las respuestas en un solo lugar y acepta la cotización que mejor se adapte a tu proyecto.',

  // Advertising, plans, FAQ, footer, and privacy
  'Regional Advertising': 'Publicidad regional',
  'Promote your seller profile or products to qualified buyers in a selected city, state, or country while organic marketplace relevance remains part of every ranking.': 'Promociona tu perfil o tus productos ante compradores calificados de una ciudad, estado o país, manteniendo la relevancia orgánica del mercado en cada clasificación.',
  'Local targeting': 'Segmentación local',
  'Choose the city, state, or country where your campaign should compete.': 'Elige la ciudad, estado o país donde competirá tu campaña.',
  'Priority visibility': 'Visibilidad prioritaria',
  'Eligible paid campaigns receive a controlled boost in matching markets.': 'Las campañas pagadas elegibles reciben un impulso controlado en los mercados coincidentes.',
  'Transparent promotion': 'Promoción transparente',
  'Every paid placement is clearly identified as sponsored.': 'Cada ubicación pagada se identifica claramente como patrocinada.',
  'Active paid campaigns': 'Campañas pagadas activas',
  'Sponsored Products for Your Market': 'Productos patrocinados para tu mercado',
  'Advertise Your Business': 'Promociona tu empresa',
  'Regional advertising benefits': 'Beneficios de la publicidad regional',
  'Eulua Premium Account': 'Cuenta Eulua Premium',
  'Turn marketplace activity into better business decisions.': 'Convierte la actividad del mercado en mejores decisiones empresariales.',
  'Start free. Upgrade when data can help you grow.': 'Comienza gratis. Mejora el plan cuando los datos puedan ayudarte a crecer.',
  'Product performance': 'Rendimiento del producto',
  'Historical impressions, clicks, CTR and average interaction time.': 'Impresiones históricas, clics, CTR y tiempo promedio de interacción.',
  'Real search demand': 'Demanda real de búsqueda',
  'Related searches and demand across subcategory, category and sector.': 'Búsquedas relacionadas y demanda por subcategoría, categoría y sector.',
  'Regional price intelligence': 'Inteligencia regional de precios',
  'Similar-product counts and average prices by city, state, country and globally.': 'Cantidad de productos similares y precios promedio por ciudad, estado, país y a nivel global.',
  'Lower sales commission': 'Menor comisión de venta',
  'Lite reduces the rate to 1.25%, Growth to 1%, and Enterprise to 0.8%, with the same US$2 equivalent minimum.': 'Lite reduce la tarifa a 1.25 %, Growth a 1 % y Enterprise a 0.8 %, con el mismo mínimo equivalente a US$2.',
  'Starter': 'Inicial',
  'Included': 'Incluido',
  'Seller account and unlimited catalog': 'Cuenta de vendedor y catálogo ilimitado',
  'Product and profile advertising': 'Publicidad de productos y perfil',
  '1.5% sales commission · $2 minimum': '1.5 % de comisión de venta · mínimo de $2',
  '1.25% sales commission · $2 minimum': '1.25 % de comisión de venta · mínimo de $2',
  'Complete analytics for every product': 'Análisis completos para cada producto',
  'Price and keyword intelligence': 'Inteligencia de precios y palabras clave',
  '0.8% sales commission · $2 minimum': '0.8 % de comisión de venta · mínimo de $2',
  '/ month': '/ mes',
  'Built for teams': 'Creado para equipos',
  'Enterprise': 'Empresarial',
  'Coming Soon': 'Próximamente',
  'One company account, shared securely by the people who manage your operation.': 'Una cuenta empresarial compartida de forma segura por quienes administran tu operación.',
  'Multiple users on the same company account': 'Varios usuarios en la misma cuenta empresarial',
  'Lower marketplace fees': 'Tarifas de mercado más bajas',
  'API integration': 'Integración con API',
  'Priority support': 'Soporte prioritario',
  'AVAILABLE NOW': 'DISPONIBLE AHORA',
  'COMING SOON · NOT YET AVAILABLE': 'PRÓXIMAMENTE · AÚN NO DISPONIBLE',
  'No commitment to upgrade.': 'Sin compromiso de mejorar el plan.',
  'Every approved seller begins on Starter.': 'Cada vendedor aprobado comienza con el plan Inicial.',
  'Start with Starter': 'Comenzar con Inicial',
  'Explore selling on Eulua': 'Descubre cómo vender en Eulua',
  'Questions, answered': 'Resolvemos tus dudas',
  'Everything you need to start with confidence': 'Todo lo que necesitas para comenzar con confianza',
  'Learn how orders, supplier confirmations, payments, and pickup work before you buy.': 'Conoce cómo funcionan los pedidos, confirmaciones de proveedores, pagos y recogidas antes de comprar.',
  'Learn more about Eulua': 'Conoce más sobre Eulua',
  'What is Eulua?': '¿Qué es Eulua?',
  'Eulua is a B2B marketplace that connects business buyers with verified suppliers and keeps product, delivery, tax, and payment details visible throughout the order process.': 'Eulua es un mercado B2B que conecta a compradores empresariales con proveedores verificados y mantiene visibles los detalles de productos, entrega, impuestos y pagos durante todo el pedido.',
  'How does buying from multiple suppliers work?': '¿Cómo funciona comprar a múltiples proveedores?',
  'You can request products from several suppliers in one order. Each supplier independently confirms inventory, shipping or pickup, taxes, and delivery timing before payment. See the full': 'Puedes solicitar productos de varios proveedores en un solo pedido. Cada proveedor confirma de forma independiente el inventario, el envío o la recogida, los impuestos y el plazo de entrega antes del pago. Consulta la',
  'shipping and pickup policy': 'política completa de envío y recogida',
  'When do I pay for an order?': '¿Cuándo pago un pedido?',
  "Payment becomes available after a supplier accepts its part of the order and confirms the final commercial terms. Review Eulua's": 'El pago queda disponible después de que un proveedor acepta su parte del pedido y confirma las condiciones comerciales finales. Consulta el',
  'secure payment process': 'proceso de pago seguro de Eulua',
  'Can I pick up an order from a supplier?': '¿Puedo recoger un pedido donde el proveedor?',
  'Yes, when pickup is available. The supplier city and country are shown before payment, and exact pickup details are released after payment is confirmed.': 'Sí, cuando la recogida está disponible. La ciudad y el país del proveedor se muestran antes del pago, y los detalles exactos se comparten después de confirmar el pago.',
  'How can my business sell on Eulua?': '¿Cómo puede mi empresa vender en Eulua?',
  'Create an account, submit the supplier application, and provide the requested business and identity details for verification.': 'Crea una cuenta, envía la solicitud de proveedor y proporciona los datos empresariales y de identidad solicitados para la verificación.',
  'Apply to become a supplier': 'Solicitar ser proveedor',
  'Information': 'Información',
  'About Us': 'Sobre nosotros',
  'Frequently Asked Questions': 'Preguntas frecuentes',
  'Shipping Policy': 'Política de envíos',
  'Payment Methods': 'Métodos de pago',
  'Privacy Policy': 'Política de privacidad',
  'Cookie Settings': 'Configuración de cookies',
  'Terms and Conditions': 'Términos y condiciones',
  'We are here to help!': '¡Estamos aquí para ayudarte!',
  'Join as a Seller': 'Únete como vendedor',
  'Returns and Refunds': 'Devoluciones y reembolsos',
  'Service to the Client:': 'Atención al cliente:',
  'Monday - Friday: 7 AM - 6 PM': 'Lunes a viernes: 7 a. m. - 6 p. m.',
  'Improve your Eulua experience': 'Mejora tu experiencia en Eulua',
  'Dismiss cookie recommendation': 'Descartar recomendación de cookies',
  'Optional cookies are currently off. Enable them to receive more relevant product recommendations and sponsored content based on the products you view.': 'Las cookies opcionales están desactivadas. Habilítalas para recibir recomendaciones de productos y contenido patrocinado más relevantes según los productos que consultes.',
  'Review your privacy choices': 'Revisar tus opciones de privacidad',
  'Not now': 'Ahora no',
  'Enable cookies': 'Habilitar cookies',
  'Your privacy choices': 'Tus opciones de privacidad',
  'Close cookie settings': 'Cerrar configuración de cookies',
  'Cookie categories': 'Categorías de cookies',
  'Eulua uses connection information to estimate an approximate location and necessary browser storage for sign-in, security, your cart, and the delivery location you choose. With your permission, we also remember viewed products and use marketplace activity to personalize recommendations and sponsored content.': 'Eulua usa información de conexión para estimar una ubicación aproximada y el almacenamiento necesario del navegador para el inicio de sesión, la seguridad, tu carrito y la ubicación de entrega que elijas. Con tu permiso, también recordamos los productos vistos y usamos la actividad del mercado para personalizar recomendaciones y contenido patrocinado.',
  'Necessary · Always active': 'Necesarias · Siempre activas',
  'Recommendations and personalization · Optional': 'Recomendaciones y personalización · Opcionales',
  'Read our Privacy Policy': 'Leer nuestra Política de privacidad',
  'Reject non-essential': 'Rechazar las no esenciales',
  'Accept cookies': 'Aceptar cookies',

  // Quote workflow shown globally
  'Project sourcing': 'Abastecimiento para proyectos',
  'Request a quote': 'Solicitar una cotización',
  'Tell verified sellers what your project needs.': 'Cuéntales a vendedores verificados qué necesita tu proyecto.',
  'Close': 'Cerrar',
  'Example: 250': "Ejemplo: 250",
  'Name of the Material/Product *': 'Nombre del material/producto *',
  'Sector *': 'Sector *',
  'Select a sector': 'Selecciona un sector',
  'Category *': 'Categoría *',
  'Select a category': 'Selecciona una categoría',
  'Quantity *': 'Cantidad *',
  'Project address': 'Dirección del proyecto',
  'Address or specific location *': 'Dirección o ubicación específica *',
  'Fulfillment *': 'Entrega o recogida *',
  'Pick up': 'Recoger',
  'I will collect the products.': 'Yo recogeré los productos.',
  'Delivery': 'Entrega',
  'Deliver to the project address.': 'Entregar en la dirección del proyecto.',
  'Project dimensions': 'Dimensiones del proyecto',
  'Optional': 'Opcional',
  'Budget': 'Presupuesto',
  'Budget currency': 'Moneda del presupuesto',
  'Display currency': 'Moneda para mostrar',
  'Above': 'Más de',
  'Available': 'Disponible',
  'Min': 'Mínimo',
  'None': 'Ninguna',
  'Add product to favorites': 'Agregar producto a favoritos',
  'Remove product from favorites': 'Quitar producto de favoritos',
  'Add to favorites': 'Agregar a favoritos',
  'Remove from favorites': 'Quitar de favoritos',
  'Favorite': 'Favorito',
  'Saved': 'Guardado',
  'Publish product': 'Publicar producto',
  'Unpublish product': 'Ocultar producto',
  'Project images *': 'Imágenes del proyecto *',
  'Add images': 'Agregar imágenes',
  'Remove': 'Eliminar',
  'Removing…': 'Eliminando…',
  'Search quote requests': 'Buscar solicitudes de cotización',
  'Code, material, buyer or location': 'Código, material, comprador o ubicación',
  'Quote request removed from your dashboard.': 'La solicitud de cotización se eliminó de tu panel.',
  'Submit request': 'Enviar solicitud',
  'Click here to request a quote for your project!': '¡Haz clic aquí para solicitar una cotización para tu proyecto!',
  'Example: Reinforced concrete': 'Ejemplo: concreto reforzado',
  'Street, building, site reference or pickup point': 'Calle, edificio, referencia del sitio o punto de recogida',
  'Area, length, height or volume': 'Área, longitud, altura o volumen',
  ...SPANISH_SITE_GAP_TRANSLATIONS,
  ...SPANISH_LEGAL_TRANSLATIONS,
  ...SPANISH_APP_TRANSLATIONS,
  ...SPANISH_SELLER_TRANSLATIONS,
  ...SPANISH_TAXONOMY_TRANSLATIONS,
};

const PATTERNS = [
  // Before the app patterns, whose generic 'Deal applies from …' would leave 'units' untranslated.
  [/^Deal applies from (\d+) units$/, 'La oferta se aplica desde $1 unidades'],
  ...SPANISH_SITE_GAP_PATTERNS,
  ...SPANISH_APP_PATTERNS,
  ...SPANISH_SELLER_PATTERNS,
  [/^Welcome, (.+)\.$/, '¡Bienvenido, $1!'],
  [/^Cart with (\d+) items?$/, 'Carrito con $1 artículos'],
  [/^Remove (.+) from your dashboard\? This only hides it for your seller account\.$/, '¿Eliminar $1 de tu panel? Solo se ocultará para tu cuenta de vendedor.'],
  [/^(\d[\d,.]*) available products\.$/, '$1 productos disponibles.'],
  [/^Browse (.+)$/, 'Explorar $1'],
  [/^(\d+) - (\d+) of (\d+)$/, '$1 - $2 de $3'],
  [/^Selected from your (.+) and matched by sector, brand, category and company\.$/, (_, sources) => {
    const names = {
      favorites: 'favoritos',
      'browsing history': 'historial de navegación',
      'recent browsing': 'navegación reciente',
      'past orders': 'pedidos anteriores',
      'marketplace activity': 'actividad en el mercado',
    };
    const translated = sources.split(', ').map((source) => names[source] || source).join(', ');
    return `Seleccionados según tus ${translated} y relacionados por sector, marca, categoría y empresa.`;
  }],
  [/^Bid must be at least ([\d,.]+)\. Each new bid must be 1\.5% higher than the current bid\.$/, 'La oferta debe ser de al menos $1. Cada nueva oferta debe superar la oferta actual en al menos un 1.5 %.'],
  [/^Leading sellers near (.+), ranked by city, state, and country relevance\.$/, 'Vendedores destacados cerca de $1, clasificados por relevancia de ciudad, estado y país.'],
  [/^Show promotion (\d+)$/, 'Mostrar promoción $1'],
  [/^Previous (.+)$/, 'Anterior: $1'],
  [/^Next (.+)$/, 'Siguiente: $1'],
];

const normalizeCountry = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

export const isSpanishSpeakingCountry = (country, countryCode) => (
  SPANISH_COUNTRY_CODES.has(String(countryCode || '').trim().toUpperCase())
  || SPANISH_COUNTRIES.has(normalizeCountry(country))
);

export const detectBrowserLanguage = (navigatorValue = typeof navigator === 'undefined' ? {} : navigator) => {
  const languages = Array.isArray(navigatorValue.languages) && navigatorValue.languages.length
    ? navigatorValue.languages
    : [navigatorValue.language];
  return languages.some((value) => {
    const [languageCode, regionCode] = String(value || '').replace('_', '-').split('-');
    return languageCode?.toLowerCase() === 'es'
      || SPANISH_COUNTRY_CODES.has(String(regionCode || '').toUpperCase());
  }) ? 'es' : 'en';
};

export const translateText = (value, language) => {
  if (language !== 'es' || typeof value !== 'string') return value;
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const text = value.trim();
  if (!text) return value;
  let translated = ES[text];
  if (!translated) {
    const pattern = PATTERNS.find(([expression]) => expression.test(text));
    if (pattern) translated = text.replace(pattern[0], pattern[1]);
  }
  return translated ? `${leading}${translated}${trailing}` : value;
};

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (value) => value,
});

const TRANSLATED_ATTRIBUTES = ['aria-label', 'placeholder', 'title', 'alt'];

const LocalizedDocument = ({ language }) => {
  const textOriginals = useRef(new WeakMap());
  const attributeOriginals = useRef(new WeakMap());

  useLayoutEffect(() => {
    const translateNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, textarea, [contenteditable="true"]')) return;
        const originals = textOriginals.current;
        const previous = originals.get(node);
        const knownValues = previous === undefined
          ? []
          : [previous, translateText(previous, 'es'), translateText(previous, 'en')];
        if (previous === undefined || !knownValues.includes(node.nodeValue)) {
          originals.set(node, node.nodeValue);
        }
        const original = originals.get(node);
        const next = translateText(original, language);
        if (node.nodeValue !== next) node.nodeValue = next;
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const elementOriginals = attributeOriginals.current;
      let originals = elementOriginals.get(node);
      if (!originals) {
        originals = {};
        elementOriginals.set(node, originals);
      }
      TRANSLATED_ATTRIBUTES.forEach((attribute) => {
        if (!node.hasAttribute(attribute)) return;
        const current = node.getAttribute(attribute);
        const previous = originals[attribute];
        const knownValues = previous === undefined
          ? []
          : [previous, translateText(previous, 'es'), translateText(previous, 'en')];
        if (previous === undefined || !knownValues.includes(current)) originals[attribute] = current;
        const next = translateText(originals[attribute], language);
        if (current !== next) node.setAttribute(attribute, next);
      });
      node.childNodes.forEach(translateNode);
    };

    const root = document.getElementById('root');
    if (!root) return undefined;
    translateNode(root);

    const observer = new MutationObserver((mutations) => {
      observer.disconnect();
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') translateNode(mutation.target);
        if (mutation.type === 'attributes') translateNode(mutation.target);
        mutation.addedNodes.forEach(translateNode);
      });
      observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: TRANSLATED_ATTRIBUTES });
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: TRANSLATED_ATTRIBUTES });
    return () => observer.disconnect();
  }, [language]);

  return null;
};

// urlLanguage comes from the current localized URL (see App.js's
// AppLanguageProvider, which reads useLocation() and passes it down) — kept as a prop rather than
// calling useLocation() here so LanguageProvider stays usable outside a Router
// (component tests render it standalone).
export const LanguageProvider = ({ children, urlLanguage = null }) => {
  const {
    getValidAccessToken,
    loading: authLoading,
    location,
    locationReady,
    refreshAccessToken,
    user,
  } = useContext(UserAuthContext);
  const pathLanguage = urlLanguage;
  const [language, setLanguageState] = useState(() => {
    if (pathLanguage === 'es') return 'es';
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === 'es' || stored === 'en' ? stored : detectBrowserLanguage();
  });
  const pendingLanguageSelection = useRef(null);
  const authenticatedUserId = user?.id || user?.userid || user?.idusers;

  // A /es URL is authoritative for Spanish. For unprefixed URLs, preserve the
  // stored selection so legacy internal links can be normalized without
  // unexpectedly changing the interface language.
  useEffect(() => {
    if (pendingLanguageSelection.current) {
      if (pendingLanguageSelection.current === pathLanguage) {
        pendingLanguageSelection.current = null;
      }
      return;
    }

    if (pathLanguage === 'es') {
      if (language !== 'es') setLanguageState('es');
      if (localStorage.getItem(LANGUAGE_STORAGE_KEY) !== 'es') {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, 'es');
      }
      return;
    }

    // An unprefixed link may have been authored without localization. Preserve
    // the selected Spanish preference so App.js can normalize that navigation
    // to /es instead of silently changing the interface back to English.
    if (localStorage.getItem(LANGUAGE_STORAGE_KEY) === 'en' && language !== 'en') {
      setLanguageState('en');
    }
  }, [pathLanguage, language]);

  useEffect(() => {
    if (pathLanguage === 'es' || localStorage.getItem(LANGUAGE_STORAGE_KEY) || !locationReady) return;
    if (isSpanishSpeakingCountry(location?.country, location?.countryCode)) setLanguageState('es');
  }, [pathLanguage, location, locationReady]);

  useEffect(() => {
    document.documentElement.lang = language;
    if (authLoading || !authenticatedUserId) return undefined;
    const controller = new AbortController();
    const saveLanguage = async () => {
      const token = getValidAccessToken
        ? await getValidAccessToken()
        : localStorage.getItem('token');
      if (!token || controller.signal.aborted) return;
      let response = await fetch(apiUrl('/api/account/language'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ language }),
        signal: controller.signal,
      });
      if (response.status === 401 && refreshAccessToken && await refreshAccessToken()) {
        const refreshedToken = localStorage.getItem('token');
        if (!refreshedToken || controller.signal.aborted) return;
        response = await fetch(apiUrl('/api/account/language'), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${refreshedToken}` },
          body: JSON.stringify({ language }),
          signal: controller.signal,
        });
      }
    };
    saveLanguage().catch((error) => {
      if (error.name !== 'AbortError') console.error('Could not sync account language:', error);
    });
    return () => controller.abort();
  }, [authLoading, authenticatedUserId, getValidAccessToken, language, refreshAccessToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const originalConfirm = window.confirm;
    const originalAlert = window.alert;
    const originalPrompt = window.prompt;
    const localizedConfirm = typeof originalConfirm === 'function'
      ? (message) => originalConfirm.call(window, translateText(String(message ?? ''), language))
      : null;
    const localizedAlert = typeof originalAlert === 'function'
      ? (message) => originalAlert.call(window, translateText(String(message ?? ''), language))
      : null;
    const localizedPrompt = typeof originalPrompt === 'function'
      ? (message, defaultValue) => originalPrompt.call(
        window,
        translateText(String(message ?? ''), language),
        defaultValue,
      )
      : null;

    if (localizedConfirm) window.confirm = localizedConfirm;
    if (localizedAlert) window.alert = localizedAlert;
    if (localizedPrompt) window.prompt = localizedPrompt;

    return () => {
      if (localizedConfirm && window.confirm === localizedConfirm) window.confirm = originalConfirm;
      if (localizedAlert && window.alert === localizedAlert) window.alert = originalAlert;
      if (localizedPrompt && window.prompt === localizedPrompt) window.prompt = originalPrompt;
    };
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = nextLanguage === 'es' ? 'es' : 'en';
    pendingLanguageSelection.current = normalized;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    setLanguageState(normalized);
  }, []);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: (text) => translateText(text, language),
  }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <LocalizedDocument language={language} />
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

// Returns a function that prefixes an internal path with /es when the current
// language is Spanish, so links don't bounce through LocalizedRouteSynchronizer.
export const useLocalizedPath = () => {
  const { language } = useLanguage();
  return useCallback((path) => withLanguagePrefix(path, language), [language]);
};

export default LanguageContext;
